import { ref, computed, onMounted } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'

export interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: any
  hasRequiredScope: boolean
  error: string | null
}

export const useAuth = () => {
  const { 
    isAuthenticated, 
    isLoading, 
    user, 
    loginWithRedirect, 
    logout, 
    getAccessTokenSilently,
    error: auth0Error
  } = useAuth0()
  
  const hasRequiredScope = ref(false)
  const error = ref<string | null>(null)
  const accessToken = ref<string | null>(null)

  const requiredPermission = import.meta.env.VITE_REQUIRED_SCOPE || 'delivery:access'

  // Check if user has required scope
  const checkScope = async () => {
    if (!isAuthenticated.value) {
      hasRequiredScope.value = false
      return
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: requiredPermission // still request it
        }
      })
      
      accessToken.value = token
      
      // Decode JWT to check scopes (simple implementation)
      const payload = JSON.parse(atob(token.split('.')[1]))
      const permissions = Array.isArray(payload.permissions) ? payload.permissions : []
      
      hasRequiredScope.value = permissions.includes(requiredPermission)
      
      if (!hasRequiredScope.value) {
        error.value = 'You do not have the required permissions to access this application.'
      } else {
        error.value = null
      }
    } catch (err) {
      console.error('Error checking scope:', err)
      hasRequiredScope.value = false
      error.value = 'Failed to verify permissions.'
    }
  }

  const authState = computed<AuthState>(() => ({
    isAuthenticated: isAuthenticated.value,
    isLoading: isLoading.value,
    user: user.value,
    hasRequiredScope: hasRequiredScope.value,
    error: error.value || (auth0Error.value?.message || null)
  }))

  const login = () => {
    loginWithRedirect({
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        scope: `openid profile email ${requiredPermission}`
      }
    })
  }

  const logoutUser = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    })
  }

  const getToken = async (): Promise<string | null> => {
    if (!isAuthenticated.value) {
      return null
    }

    try {
      if (!accessToken.value) {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            scope: requiredPermission
          }
        })
        accessToken.value = token
      }
      return accessToken.value
    } catch (err) {
      console.error('Error getting access token:', err)
      return null
    }
  }

  onMounted(() => {
    // Check scope when component mounts and user is authenticated
    if (isAuthenticated.value) {
      checkScope()
    }
  })

  // Watch for authentication changes
  const unwatchAuth = computed(() => isAuthenticated.value)
  unwatchAuth.value && checkScope()

  return {
    authState,
    login,
    logout: logoutUser,
    getToken,
    checkScope
  }
} 